/**
 * Dal camper storto ai gradini da usare.
 *
 * CONVENZIONI DEI SEGNI (valgono ovunque nella livella)
 *   roll  > 0  il lato SINISTRO e' piu' in alto
 *   pitch > 0  il MUSO e' piu' in alto
 *
 * MODELLO
 * I due cunei si possono mettere sotto due ruote qualsiasi, quindi non basta piu' la
 * formula del singolo asse: si guarda l'assetto della scocca come il piano che meglio
 * approssima i quattro punti d'appoggio.
 *
 *   variazione di rollio      = atan( ((h_AS + h_PS) - (h_AD + h_PD)) / (2 * carreggiata) )
 *   variazione di beccheggio  = atan( ((h_AS + h_AD) - (h_PS + h_PD)) / (2 * passo) )
 *
 * Sul beccheggio e' identica alla formula del singolo asse usata prima. Sul rollio da'
 * la meta' quando si alza una ruota sola — che e' anche il motivo per cui la previsione
 * puramente geometrica sovrastimava il rollio.
 *
 * I fattori di taratura moltiplicano queste variazioni: rappresentano quanta parte
 * della correzione geometrica il camper realizza davvero, il resto essendo assorbito
 * dalle sospensioni.
 *
 * PERCHE' CONTA DOVE SI METTONO
 * Con i due cunei sullo stesso ASSE gli effetti sul rollio si sottraggono (conta la
 * differenza fra i due), e per giunta l'asse si alza in media, cioe' ogni correzione
 * di rollio inquina il beccheggio. Con i due cunei sullo stesso LATO il fianco sale in
 * blocco: gli effetti si sommano e il beccheggio non si muove affatto.
 *
 * La diagonale (una anteriore e la posteriore opposta) e' esclusa di proposito: dava un
 * guadagno trascurabile ed e' la disposizione piu' facile da sbagliare in piazzola.
 *
 * SCELTA DEI GRADINI
 * Non si arrotonda l'altezza ideale al gradino piu' vicino: si provano tutte le
 * combinazioni possibili e si tiene la migliore. Due motivi concreti:
 *   - una ruota non si puo' ABBASSARE. L'altezza ideale e' spesso negativa da un
 *     lato, e arrotondarla darebbe un numero che non si puo' realizzare;
 *   - con gradini non equidistanti l'arrotondamento indipendente dei due lati puo'
 *     peggiorare il totale, perche' i due errori si sommano invece di compensarsi.
 * Le combinazioni sono un paio di centinaia: costano nulla.
 */
const DEG = 180 / Math.PI;
/** Sotto questa inclinazione il camper e' in piano per qualunque uso pratico
 *  (frigo ad assorbimento, dormire, sportelli che non si chiudono da soli). */
export const LEVEL_DEG = 0.3;
/** Il rollio pesa piu' del beccheggio: coricati si sente subito, e il frigo ad
 *  assorbimento e' piu' sensibile all'inclinazione laterale. A parita' di gradi
 *  residui la livella preferisce quindi sistemare il rollio. */
const W_ROLL = 1.0;
const W_PITCH = 0.6;
/** A parita' di risultato meglio salire meno: piu' basso e' l'appoggio, piu'
 *  stabile e' il mezzo e piu' facile e' la manovra. */
const HEIGHT_TIEBREAK = 1e-4;
/** Sempre a parita' di risultato, meglio due cunei della stessa altezza: i quattro
 *  punti d'appoggio restano complanari e nessuna ruota si scarica. */
const WARP_TIEBREAK = 1e-4;
/** Oltre questa differenza fra i due cunei la scocca si "svergola" in modo marcato e
 *  una ruota si scarica — quella diagonale rispetto alla piu' alta, dove per giunta
 *  puo' agire il freno a mano. */
const THREE_WHEEL_WARN_CM = 8;
export const WHEEL_LABELS = {
    FL: 'Anteriore SX',
    FR: 'Anteriore DX',
    RL: 'Posteriore SX',
    RR: 'Posteriore DX',
};
export const PLACEMENT_GROUPS = {
    front: ['FL', 'FR'],
    rear: ['RL', 'RR'],
    left: ['FL', 'RL'],
    right: ['FR', 'RR'],
};
export const PLACEMENT_GROUP_LABELS = {
    front: 'Cunei sotto le ruote ANTERIORI',
    rear: 'Cunei sotto le ruote POSTERIORI',
    left: 'Cunei sul LATO SINISTRO',
    right: 'Cunei sul LATO DESTRO',
};
/** Le opzioni di un cuneo: la ruota a terra e' sempre una scelta, non un gradino. */
const options = (steps) => [0, ...steps];
function outcome(rollBefore, pitchBefore, h, cfg) {
    const dRoll = Math.atan((h.FL + h.RL - h.FR - h.RR) / (2 * cfg.track)) * DEG * cfg.gainRoll;
    const dPitch = Math.atan((h.FL + h.FR - h.RL - h.RR) / (2 * cfg.wheelbase)) * DEG * cfg.gainPitch;
    return { roll: rollBefore + dRoll, pitch: pitchBefore + dPitch };
}
const cost = (after, hA, hB) => (W_ROLL * after.roll) ** 2 +
    (W_PITCH * after.pitch) ** 2 +
    HEIGHT_TIEBREAK * (hA + hB) +
    WARP_TIEBREAK * Math.abs(hA - hB);
export function solve(input) {
    const cfg = {
        track: input.track,
        wheelbase: input.wheelbase,
        gainRoll: input.gainRoll ?? 1,
        gainPitch: input.gainPitch ?? 1,
    };
    const roll = input.roll;
    const pitch = input.pitch;
    if (Math.abs(roll) <= LEVEL_DEG && Math.abs(pitch) <= LEVEL_DEG) {
        return {
            level: true,
            group: 'front',
            placements: [
                { wheel: 'FL', step: 0, height: 0, wedge: 'A' },
                { wheel: 'FR', step: 0, height: 0, wedge: 'B' },
            ],
            before: { roll, pitch },
            after: { roll, pitch },
            predicted: { rollDelta: 0, pitchDelta: 0 },
            reachable: true,
            warnings: [],
        };
    }
    const optsA = options(input.wedgeA);
    const optsB = options(input.wedgeB);
    let best = null;
    const stepOf = (steps, height) => height === 0 ? 0 : steps.indexOf(height) + 1;
    for (const group of Object.keys(PLACEMENT_GROUPS)) {
        const [w1, w2] = PLACEMENT_GROUPS[group];
        // I due cunei possono avere gradini diversi, quindi conta anche quale dei due
        // va su quale ruota: si provano entrambe le assegnazioni.
        const assignments = [
            ['A', 'B'],
            ['B', 'A'],
        ];
        for (const [n1, n2] of assignments) {
            const steps1 = n1 === 'A' ? input.wedgeA : input.wedgeB;
            const steps2 = n2 === 'A' ? input.wedgeA : input.wedgeB;
            const opts1 = n1 === 'A' ? optsA : optsB;
            const opts2 = n2 === 'A' ? optsA : optsB;
            for (const h1 of opts1) {
                for (const h2 of opts2) {
                    const h = { FL: 0, FR: 0, RL: 0, RR: 0 };
                    h[w1] = h1;
                    h[w2] = h2;
                    const after = outcome(roll, pitch, h, cfg);
                    const c = cost(after, h1, h2);
                    if (best && c >= best.c)
                        continue;
                    best = {
                        c,
                        group,
                        after,
                        hA: h1,
                        hB: h2,
                        placements: [
                            { wheel: w1, step: stepOf(steps1, h1), height: h1, wedge: n1 },
                            { wheel: w2, step: stepOf(steps2, h2), height: h2, wedge: n2 },
                        ],
                    };
                }
            }
        }
    }
    // Impossibile: options() restituisce sempre almeno [0], quindi il ciclo gira.
    if (!best)
        throw new Error('Nessuna combinazione di gradini valutabile');
    const warnings = [];
    const reachable = Math.abs(best.after.roll) <= LEVEL_DEG && Math.abs(best.after.pitch) <= LEVEL_DEG;
    if (!reachable) {
        const parts = [];
        if (Math.abs(best.after.roll) > LEVEL_DEG)
            parts.push('rollio');
        if (Math.abs(best.after.pitch) > LEVEL_DEG)
            parts.push('beccheggio');
        warnings.push({
            kind: 'out-of-range',
            text: `I cunei non bastano a raddrizzare del tutto ${parts.join(' e ')}. Questo è il meglio ottenibile: valuta di spostare il camper.`,
        });
    }
    // Quando i due cunei hanno altezze diverse i quattro punti d'appoggio non sono più
    // complanari, e la ruota diagonale rispetto alla più alta si scarica. Vale sia con i
    // cunei sullo stesso asse sia sullo stesso lato: conta la differenza fra i due.
    const warp = Math.abs(best.hA - best.hB);
    if (warp >= THREE_WHEEL_WARN_CM) {
        warnings.push({
            kind: 'three-wheel',
            text: `I due cunei differiscono di ${warp.toFixed(1)} cm: il camper scaricherà peso su una ruota. Cunea anche le altre due ruote prima di lasciarlo lì.`,
        });
    }
    return {
        level: false,
        group: best.group,
        placements: best.placements,
        before: { roll, pitch },
        after: best.after,
        // Serve all'autotaratura: e' la variazione che la livella promette all'utente,
        // da confrontare con quella davvero misurata dopo la salita sui cunei.
        predicted: {
            rollDelta: roll - best.after.roll,
            pitchDelta: pitch - best.after.pitch,
        },
        reachable,
        warnings,
    };
}
//# sourceMappingURL=leveling.js.map